import {
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { readableDate } from "utils/readableDate";
import { Edit, Delete } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { Transaction } from "types/transactionGroup";
import { Modal } from "components/common/Modal";
import { Client } from "types/client";

interface TransactionGroupTableProps {
  transactions: Transaction[];
  client?: Client;
  deleteTransactionGroup: (transactionGroupId: string) => void;
}

interface ProcessedTransaction extends Transaction {
  style: {
    color: string;
    textDecoration: string;
    chip: { label: string; show: boolean };
  };
  formattedAmount: React.ReactNode;
}

const getTransactionStyle = (transaction: Transaction) => {
  const isCredit =
    transaction.transactionType === "credit" ||
    transaction.type === "Earnings Credit" ||
    transaction.type === "Donation Credit" ||
    transaction.type === "Asset Retrieval Credit";
  const amountValue = Math.abs(transaction.displayAmountInCents / 100.0);

  const styleRules = {
    waived: {
      condition: transaction.waived,
      color: "text.primary",
      textDecoration: "line-through",
      chip: { label: "FEES WAIVED", show: true },
    },
    laptopRetrievalCancelled: {
      condition: transaction.type === "Laptop Retrieval Order" && transaction.creditTransaction,
      color: "text.primary",
      textDecoration: "line-through",
      chip: { label: "CANCELLED", show: true },
    },
    laptopRetrievalGroupPartialRefund: {
      condition: transaction.type === "Laptop Retrieval Group Order" && transaction.creditTransaction,
      color: "error.main",
      textDecoration: "none",
      chip: {
        label: `REFUNDED [$${transaction.creditTransaction || 0}]`,
        show: true,
      },
    },
    zeroAmount: {
      condition: amountValue === 0,
      color: "text.primary",
      textDecoration: "none",
      chip: { label: "", show: false },
    },
    credit: {
      condition: isCredit,
      color: "success.main",
      textDecoration: "none",
      chip: { label: "", show: false },
    },
    debit: {
      condition: true,
      color: "error.main",
      textDecoration: "none",
      chip: { label: "", show: false },
    },
  };

  const matchedRule = Object.values(styleRules).find((rule) => rule.condition);

  return {
    color: matchedRule?.color || "text.primary",
    textDecoration: matchedRule?.textDecoration || "none",
    chip: matchedRule?.chip || { label: "", show: false },
  };
};

const formatTransactionAmount = (transaction: Transaction): React.ReactNode => {
  const isPartialRefund = transaction.type === "Laptop Retrieval Group Order" && transaction.creditTransaction;

  if (isPartialRefund && transaction.debitTransaction && transaction.creditTransaction) {
    const netAmount = transaction.debitTransaction - transaction.creditTransaction;
    const originalAmount = transaction.debitTransaction;
    return (
      <span>
        -${netAmount} <span style={{ textDecoration: "line-through", color: "#999" }}>-${originalAmount}</span>
      </span>
    );
  }

  const absAmount = Math.abs(transaction.displayAmountInCents / 100.0);

  if (transaction.displayAmountInCents === 0) {
    return `$${absAmount}`;
  }

  if (transaction.transactionType === "credit") {
    return `+$${absAmount}`;
  }

  return `-$${absAmount}`;
};

function TransactionGroupTable({ transactions, client, deleteTransactionGroup }: TransactionGroupTableProps) {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  const processedTransactions = useMemo((): ProcessedTransaction[] => {
    if (!transactions) return [];

    return transactions.map((transaction) => ({
      ...transaction,
      style: getTransactionStyle(transaction),
      formattedAmount: formatTransactionAmount(transaction),
    }));
  }, [transactions]);

  const handleDeleteClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedTransaction) {
      deleteTransactionGroup(selectedTransaction.id);
      setDeleteModalOpen(false);
      setSelectedTransaction(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedTransaction(null);
  };

  return (
    <div>
      <TableContainer
        sx={{
          mt: 2,
        }}
      >
        <Table sx={{ ".MuiTableRow-root:last-child td, .MuiTableRow-root:last-child th": { border: 0 } }}>
          <TableHead sx={{ ".MuiTableCell-root": { color: "text.disabled", borderBottom: 0 } }}>
            <TableRow>
              <TableCell>DATE POSTED</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>DESCRIPTION</TableCell>
              <TableCell>AMOUNT</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {processedTransactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>{readableDate(transaction.postedAt)}</TableCell>
                <TableCell>
                  <Typography variant="body2">{transaction.type}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{transaction.description}</Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      color: transaction.style.color,
                      textDecoration: transaction.style.textDecoration,
                    }}
                  >
                    {transaction.formattedAmount}
                  </Typography>
                </TableCell>
                <TableCell>
                  {transaction.style.chip.show && (
                    <Chip
                      style={{ color: "white", fontWeight: 600, marginLeft: "10px", fontSize: "12px" }}
                      color="secondary"
                      label={transaction.style.chip.label}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    onClick={() => {
                      navigate(
                        `/finance/clients/${uuid}/transaction_groups/${transaction.type.split(" ").join("")}/${
                          transaction.id
                        }`,
                      );
                    }}
                  >
                    <Edit />
                  </Button>
                  <Button onClick={() => handleDeleteClick(transaction)} color="primary">
                    <Delete />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal open={deleteModalOpen} setOpen={setDeleteModalOpen} onClose={handleDeleteCancel} width="600px">
        <div style={{ textAlign: "center" }}>
          <Delete sx={{ fontSize: 48, mb: 2 }} color="primary" />
          <Typography fontSize={22} fontWeight={800} gutterBottom>
            Archive Transaction
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Are you sure you want to archive {client?.name} {selectedTransaction?.type || ""} for{" "}
            {selectedTransaction?.description || ""}?
          </Typography>

          {selectedTransaction && (
            <div
              style={{
                textAlign: "left",
                marginBottom: "24px",
                padding: "16px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Included transactions:
              </Typography>
              {selectedTransaction.debitTransaction && selectedTransaction.debitTransaction !== 0 && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Debit:</strong> ${Math.abs(selectedTransaction.debitTransaction)}
                </Typography>
              )}
              {selectedTransaction.creditTransaction && selectedTransaction.creditTransaction !== 0 && (
                <Typography variant="body2">
                  <strong>Credit:</strong> ${Math.abs(selectedTransaction.creditTransaction)}
                </Typography>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: "16px", justifyContent: "center" }}>
            <Button variant="outlined" onClick={handleDeleteCancel}>
              NO, GO BACK
            </Button>
            <Button variant="contained" color="primary" onClick={handleDeleteConfirm}>
              YES, ARCHIVE
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default TransactionGroupTable;
